/**
 * Types du terrain : affectations, missions, signalements.
 */

/** Ce qu'un ouvrier voit d'un chantier. Aucun montant, et c'est le point. */
export interface Mission {
  projectId: string;
  projectName: string;
  projectStatus: string;
  siteAddress: string | null;
  sitePostalCode: string | null;
  siteCity: string | null;
  /** Sur un chantier neuf, l'adresse ne mène parfois nulle part : c'est un terrain. */
  siteGps: { lat: number; lng: number } | null;
  roleOnSite: string | null;
  startsOn: string;
  endsOn: string | null;
  notes: string | null;
  progressPercent: number | null;
  /** Voir `coversDay` : le week-end ne se travaille que s'il est coché. */
  worksSaturday: boolean;
  worksSunday: boolean;
  /** Voir `DayPart` : la journée entière, le matin ou l'après-midi. */
  dayPart: DayPart;
}

export interface Assignment {
  id: string;
  projectId: string;
  projectName: string | null;
  employeeId: string;
  employeeName: string | null;
  startsOn: string;
  endsOn: string | null;
  roleOnSite: string | null;
  notes: string | null;
  /** Voir `coversDay` : le week-end ne se travaille que s'il est coché. */
  worksSaturday: boolean;
  worksSunday: boolean;
  /** Voir `DayPart` : la journée entière, le matin ou l'après-midi. */
  dayPart: DayPart;
}

export type FieldReportKind =
  | "material_shortage"
  | "incident"
  | "access"
  | "defect"
  | "other"
  /**
   * Le compagnon dit où en est son ouvrage.
   *
   * Rangé parmi les signalements et non dans une table à part : c'est déjà la
   * boîte de réception du terrain, et en ouvrir une seconde obligerait le
   * patron à surveiller deux endroits — donc à n'en surveiller aucun.
   *
   * Ce n'est pas l'avancement, c'est ce que le terrain en dit. Il le devient
   * quand un responsable applique la déclaration, parce que l'avancement mène
   * à une facture : la file du soir en tire le signal « établir une
   * situation ».
   */
  | "progress";

/** Le libellé d'une remontée, côté encadrement — le bureau comme le chef de chantier. */
export const FIELD_REPORT_KIND_LABELS: Record<FieldReportKind, string> = {
  material_shortage: "Rupture de matériel",
  incident: "Incident",
  access: "Accès bloqué",
  defect: "Reprise à prévoir",
  other: "Signalement",
  progress: "Avancement déclaré",
};

export type FieldReportSeverity = "info" | "warning" | "critical";
export type FieldReportStatus = "open" | "acknowledged" | "resolved";

export interface FieldReport {
  id: string;
  projectId: string;
  projectName: string | null;
  employeeId: string | null;
  employeeName: string | null;
  kind: FieldReportKind;
  severity: FieldReportSeverity;
  message: string;
  status: FieldReportStatus;
  createdAt: string;
  resolvedAt: string | null;

  /** Renseignés pour un signalement `progress`, nuls pour tous les autres. */
  phaseId: string | null;
  phaseName: string | null;
  declaredProgressPercent: number | null;
}

/** Une phase, telle que l'ouvrier a le droit de la voir. */
export interface MyPhase {
  id: string;
  name: string;
  sortOrder: number;
  progressPercent: number;
  status: string;
  /** Le corps de métier de la phase, sur un chantier découpé en lots. */
  lotName: string | null;
}

/**
 * Quelqu'un que l'on peut appeler depuis le chantier.
 *
 * Trois champs, et pas un de plus : c'est une projection étroite de
 * `public.users`, que l'ouvrier ne lit pas — voir `my_call_contacts()`. Le
 * numéro est toujours renseigné, la base écarte ceux qui n'en ont pas : un
 * bouton qui ne compose rien est pire que pas de bouton.
 */
export interface CallContact {
  userId: string;
  fullName: string;
  /** `patron` ou `conducteur`. Sert à dire qui on appelle, pas à autoriser. */
  role: string;
  phone: string;
}

/**
 * Une déclaration que le patron n'a pas encore appliquée.
 *
 * Distinguée d'un signalement ordinaire parce qu'elle appelle un geste
 * différent : les autres se closent, celle-ci s'**applique** — elle porte un
 * chiffre qui va s'inscrire sur la phase.
 */
export function isPendingProgress(report: FieldReport): boolean {
  return report.kind === "progress" && report.status !== "resolved";
}

/**
 * L'adresse du chantier en une ligne, telle qu'on la donnerait à un chauffeur.
 *
 * Rendue vide plutôt qu'approximative quand rien n'est saisi : c'est ce vide
 * qui fait disparaître les boutons de navigation, et il vaut mieux qu'ils
 * disparaissent que d'ouvrir une carte du monde entière.
 */
function siteAddressLine(mission: Mission): string {
  return [mission.siteAddress, mission.sitePostalCode, mission.siteCity]
    .filter((p) => p && p.trim())
    .join(" ")
    .trim();
}

/**
 * Un lien d'itinéraire, à partir de ce qu'on a.
 *
 * Les coordonnées d'abord : sur un chantier neuf, « lieu-dit Le Pré, 56000 »
 * n'ouvre rien dans une application de navigation, alors qu'un couple de
 * coordonnées y mène toujours. L'adresse ne sert que de repli.
 *
 * Renvoie `null` plutôt qu'un lien vide quand on n'a ni l'un ni l'autre : un
 * bouton d'itinéraire qui ouvre une carte du monde entière est pire que pas
 * de bouton.
 */
export function directionsUrl(mission: Mission): string | null {
  if (mission.siteGps) {
    const { lat, lng } = mission.siteGps;
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }

  const adresse = siteAddressLine(mission);
  if (!adresse) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(adresse)}`;
}

/**
 * Le même trajet, mais dans Waze — l'application que conduisent les compagnons.
 *
 * `waze.com/ul` est un lien universel : sur un téléphone où Waze est installé,
 * il ouvre l'application ; ailleurs il retombe sur la carte web. Un seul lien
 * suffit donc, et il n'y a rien à détecter côté navigateur.
 *
 * `navigate=yes` est ce qui fait la différence entre « voici le chantier sur
 * une carte » et « le guidage démarre » : sans lui, il reste un appui à faire,
 * et c'est un appui de trop quand on est déjà au volant.
 *
 * Même règle que pour l'itinéraire : coordonnées d'abord, adresse en repli,
 * `null` quand on n'a ni l'une ni l'autre.
 */
export function wazeUrl(mission: Mission): string | null {
  if (mission.siteGps) {
    const { lat, lng } = mission.siteGps;
    return `https://www.waze.com/ul?ll=${lat}%2C${lng}&navigate=yes`;
  }

  const adresse = siteAddressLine(mission);
  if (!adresse) return null;
  return `https://www.waze.com/ul?q=${encodeURIComponent(adresse)}&navigate=yes`;
}

/**
 * L'ordre dans lequel le patron doit voir les signalements.
 *
 * Le plus grave d'abord, puis le plus ancien : un signalement critique de ce
 * matin passe avant un signalement critique d'hier seulement si l'on trie mal.
 * Ce qui dort depuis trois jours est le vrai problème — c'est celui que
 * personne n'a traité.
 */
const GRAVITE: Record<FieldReportSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

export function sortFieldReports(reports: readonly FieldReport[]): FieldReport[] {
  return [...reports].sort((a, b) => {
    const parGravite = GRAVITE[a.severity] - GRAVITE[b.severity];
    if (parGravite !== 0) return parGravite;
    return a.createdAt.localeCompare(b.createdAt);
  });
}

/**
 * La **période** court-elle à cette date ?
 *
 * Calendaire, week-end compris. C'est la bonne question pour une absence — un
 * congé du vendredi au lundi couvre le samedi — et pour un droit : un ouvrier
 * présent un samedi non prévu doit pouvoir signaler un accident. Ce n'est
 * **pas** la question « est-il attendu sur le chantier ce jour-là » : celle-là
 * est `coversDay`.
 */
export function isActiveOn(
  assignment: Pick<Assignment, "startsOn" | "endsOn">,
  isoDate: string,
): boolean {
  if (assignment.startsOn > isoDate) return false;
  return assignment.endsOn == null || assignment.endsOn >= isoDate;
}

export type WeekendDay = "saturday" | "sunday";

/**
 * La part de la journée qu'occupe une affectation.
 *
 * Le patron, le 17 septembre : « il faut séparer la journée en deux, le matin
 * et l'après-midi. Un ouvrier peut être sur plusieurs chantiers dans une
 * journée. » Le planning ne connaissait que la journée entière : deux
 * affectations le même jour déclenchaient l'alerte du double chantier, alors
 * qu'une reprise le matin et le chantier principal l'après-midi est l'emploi
 * du temps ordinaire d'un maçon.
 *
 * Une affectation porte donc sa part — la journée par défaut, qui est ce que
 * toutes les affectations existantes voulaient dire — et seul un recouvrement
 * **au même moment** est un conflit.
 */
export type DayPart = "journee" | "matin" | "apres_midi";
export type HalfDay = Exclude<DayPart, "journee">;

/** Les deux moitiés, dans l'ordre de la journée. */
export const HALF_DAYS: readonly HalfDay[] = ["matin", "apres_midi"];

/** Les trois choix, dans l'ordre où un écran les propose. */
export const DAY_PARTS: readonly DayPart[] = ["journee", "matin", "apres_midi"];

const DAY_PART_LABELS: Record<DayPart, string> = {
  journee: "Journée",
  matin: "Matin",
  apres_midi: "Après-midi",
};

export function dayPartLabel(part: DayPart): string {
  return DAY_PART_LABELS[part];
}

/** Les demi-journées qu'occupe une part. */
export function halvesOf(part: DayPart): readonly HalfDay[] {
  return part === "journee" ? HALF_DAYS : [part];
}

/**
 * La part qui réunit des demi-journées : le matin et l'après-midi font une
 * journée. `null` quand il n'y en a aucune.
 */
export function dayPartOf(halves: Iterable<HalfDay>): DayPart | null {
  const set = new Set(halves);
  if (set.has("matin") && set.has("apres_midi")) return "journee";
  if (set.has("matin")) return "matin";
  if (set.has("apres_midi")) return "apres_midi";
  return null;
}

/** Samedi ou dimanche, sinon `null`. Calculé en UTC sur une date nue. */
export function weekendDayOf(isoDate: string): WeekendDay | null {
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`).getUTCDay();
  return d === 6 ? "saturday" : d === 0 ? "sunday" : null;
}

/**
 * Cette affectation fait-elle venir quelqu'un sur le chantier ce jour-là ?
 *
 * Le patron : « quand on affecte un employé sur un chantier, des présences
 * sont mises pour le samedi et dimanche. Les gars peuvent faire des heures
 * supplémentaires, mais il faut pouvoir le gérer. »
 *
 * Une affectation est une période, et tout le produit la lisait comme une
 * présence **chaque** jour de cette période. La règle vit désormais ici, et
 * nulle part ailleurs : **les jours ouvrés de la période, plus le samedi ou le
 * dimanche seulement s'ils sont cochés.** Des heures supplémentaires un samedi
 * précis sont une affectation d'un jour, ce samedi-là, samedi coché.
 */
export function coversDay(
  assignment: Pick<Assignment, "startsOn" | "endsOn" | "worksSaturday" | "worksSunday">,
  isoDate: string,
): boolean {
  if (!isActiveOn(assignment, isoDate)) return false;
  const weekend = weekendDayOf(isoDate);
  if (weekend === "saturday") return assignment.worksSaturday;
  if (weekend === "sunday") return assignment.worksSunday;
  return true;
}

/**
 * Les demi-journées où cette affectation fait venir quelqu'un ce jour-là.
 * Vide quand elle ne couvre pas le jour — voir `coversDay`, qui reste la règle
 * du week-end.
 */
export function halvesCoveredOn(
  assignment: Pick<Assignment, "startsOn" | "endsOn" | "worksSaturday" | "worksSunday" | "dayPart">,
  isoDate: string,
): readonly HalfDay[] {
  return coversDay(assignment, isoDate) ? halvesOf(assignment.dayPart) : [];
}
